import * as React from 'react';
import { useRef } from 'react';

import { translate } from '~l10n/client';

import { JoinWorkspaceEventAdapter } from '~analytics/adapters/JoinWorkspaceEventAdapter';
import { SmallButton } from '~shared/components/buttons';
import { Dialog } from '~shared/components/Dialog';
import { Divider } from '~shared/components/Divider';
import { useAutoFocus } from '~shared/components/helpers';
import TextInput from '~shared/components/TextInput';
import { toast } from '~shared/components/Toast';
import {
  TeacherApplicationContext,
  useTeacherApplicationContext,
} from '~shared/context/TeacherApplicationContext';
import { useHandleJoinWorkspaceError } from '~teacher/components/workspaces/JoinWorkspaceWithCodeDialog/useHandleJoinWorkspaceError';
import { useSocrativeRouter } from '~teacher/context/RouterContext';
import { useJoinWorkspace } from '~teacher/hook/workspaces/useJoinWorkspace';

import { isAxiosError } from '../../../../api/isAxiosError';
import { getWorkspaceByCode } from '../../../../api/workspaces/join/joinWorkspace';

const MAX_LIBRARY_CODE_LENGTH = 128;
const MIN_LIBRARY_CODE_LENGTH = 5;

function buildWorkspaceCodeFromInput(
  input: string,
  prefix: string
):  { workspaceCode: string | null; source: 'code' | 'link' } {
  const normalizedInput = input.trim().toLowerCase();

  let workspaceCode: string | null = null;
  let source: 'link' | 'code' = 'code';

  try {
    const url = new URL(normalizedInput);

    // Extract slug from /w/slug path
    const pathParts = url.pathname.split('/');
    const slug =
      pathParts.length >= 3 && pathParts[1] === 'w' ? pathParts[2] : '';

    if (slug) {
      workspaceCode = `${prefix}${slug}`;
      source = 'code';
    } else {
      // URL but not a recognized /w/<slug> pattern; keep input as-is for validation
      workspaceCode = normalizedInput;
      source = 'code';
    }
  } catch {
    // Not a valid URL, return the normalized input as-is
    workspaceCode = normalizedInput;
    source = 'code';
  }

  // Validate that the code starts with the expected prefix
  if (!workspaceCode.startsWith(prefix)) {
    return { workspaceCode: null, source };
  }

  return { workspaceCode, source };
}

interface UseHandleJoinWorkspaceParams {
  onClose: () => void;
  joinWorkspaceMutation: ReturnType<typeof useJoinWorkspace>;
}

function useHandleJoinWorkspace({
  onClose,
  joinWorkspaceMutation,
}: UseHandleJoinWorkspaceParams) {
  const router = useSocrativeRouter();
  const applicationContext = useTeacherApplicationContext();
  const handleJoinWorkspaceError = useHandleJoinWorkspaceError();

  return async (input: string) => {
    const codePrefix =
      applicationContext === TeacherApplicationContext.ShowbieAssessments
        ? 'bib-'
        : 'lib-';

    const {workspaceCode, source} = buildWorkspaceCodeFromInput(input, codePrefix);

    if (__DEV__) {
      console.log('Join workspace code: ', workspaceCode);
    }

    if (!workspaceCode) {
      toast.critical(
        translate(
          `That code won't work here — library codes start with ${codePrefix.toUpperCase()}`
        )
      );
      return;
    }

    const trimmedCode = workspaceCode.slice(codePrefix.length);

    try {
      const data = await getWorkspaceByCode({ workspaceCode: trimmedCode });
      const workspaceId = data.workspaceId;

      joinWorkspaceMutation.mutate(
        {
          workspaceCode: trimmedCode,
          workspaceId,
        },
        {
          onSuccess: () => {
            new JoinWorkspaceEventAdapter({
              workspace_id: workspaceId,
              join_method: source,
            }).sendAnalytics();

            onClose();
            router.routeToWorkspace(workspaceId);
          },
          onError: (error) => {
            if (isAxiosError(error)) {
              handleJoinWorkspaceError(error);
            } else {
              toast.critical(translate('Invalid Code.'));
            }
          },
        }
      );
    } catch (error) {
      console.error('Error joining workspace: ', error);
      toast.critical(translate('Invalid Code.'));
    }
  };
}

export interface Props {
  isOpen?: boolean;
  onClose?: () => void;
}

export function JoinWorkspaceWithCodeDialog({ isOpen, onClose }: Props) {
  const [workspaceCode, setWorkspaceCode] = React.useState<string>(null);
  const applicationContext = useTeacherApplicationContext();
  const joinWorkspaceMutation = useJoinWorkspace();

  const onWorkspaceCodeChange = (event: React.ChangeEvent<HTMLInputElement>) =>
    setWorkspaceCode(event.target.value);
  const autoFocusOnOpenCallback = useAutoFocus();

  const formRef = useRef<HTMLFormElement>(null);

  const workspaceCodePlaceholder =
    applicationContext === TeacherApplicationContext.ShowbieAssessments
      ? 'BIB-XXXXXX'
      : 'LIB-XXXXXX';

  const handleJoinWorkspace = useHandleJoinWorkspace({
    onClose,
    joinWorkspaceMutation,
  });

  return (
    <Dialog
      open={isOpen}
      onClose={onClose}
      id="join-workspace-dialog"
      title={translate('Join Library')}
    >
      <form
        ref={formRef}
        onSubmit={(event) => {
          event.preventDefault();
          if (formRef.current.checkValidity()) {
            handleJoinWorkspace(workspaceCode);
          }
        }}
      >
        <div className="pt-4 text-base leading-8 text-blue-900">
          <div className="flex flex-col gap-8">
            <div className="flex flex-col gap-4">
              <Divider />
              <div className="text-base text-blue-600">
                {translate('Create, edit, and share quizzes with your peers')}
              </div>
              <Divider />
            </div>
            <div className="flex flex-col gap-4">
              <label htmlFor="library-join-code">
                {translate('Library Join Code')}
              </label>
              <div className="flex gap-6">
                <TextInput
                  ref={autoFocusOnOpenCallback}
                  maxLength={MAX_LIBRARY_CODE_LENGTH}
                  minLength={MIN_LIBRARY_CODE_LENGTH}
                  required
                  id="library-join-code"
                  defaultValue={workspaceCode}
                  onChange={onWorkspaceCodeChange}
                  placeholder={workspaceCodePlaceholder}
                  className="flex-1 min-w-40 focus:bg-grey-50"
                />
                <SmallButton
                  type="submit"
                  isDisabled={joinWorkspaceMutation.isLoading}
                  appearance="primary"
                  className="whitespace-nowrap"
                >
                  {translate('Join Library')}
                </SmallButton>
              </div>
            </div>
          </div>
        </div>
      </form>
    </Dialog>
  );
}
